import * as React from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useBillingContext } from "@/contexts/BillingContext";

interface ServiceUnavailableDialogProps {
  isOpen: boolean;
}

const descriptionClassName =
  "font-[family-name:var(--font-primary)] text-sm font-normal leading-5 text-gray-500 dark:text-white";

/**
 * Downtime modal — matches WorkspaceRemoveNodeDialog / InstrumentSetupWizard shell.
 */
export default function ServiceUnavailableDialog({
  isOpen,
}: ServiceUnavailableDialogProps): React.ReactElement {
  const { refresh, isLoading } = useBillingContext();
  const [retrying, setRetrying] = React.useState(false);

  const onTryAgain = React.useCallback(async () => {
    setRetrying(true);
    try {
      await refresh();
    } finally {
      setRetrying(false);
    }
  }, [refresh]);

  const busy = retrying || isLoading;

  return (
    <Dialog open={isOpen} onOpenChange={() => undefined}>
      <DialogContent
        showCloseButton={false}
        data-service-unavailable-dialog=""
        className="gap-0 p-0 sm:max-w-lg"
        onPointerDownOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        <DialogHeader className="gap-2 border-b border-border px-6 py-4 pr-6 text-left">
          <DialogTitle className="font-[family-name:var(--font-primary)] text-base font-normal text-[color:var(--brand-tropical-surf)]">
            Service unavailable
          </DialogTitle>
          <DialogDescription className={descriptionClassName}>
            We&apos;re experiencing unexpected downtime. Tessera is temporarily unable to connect to
            our servers. Please try again in a few minutes. Your account and data are safe.
            <span className="block pt-2 text-gray-500 dark:text-white/80">
              Our team is aware of the issue and is working to restore the platform as quickly as
              possible.
            </span>
          </DialogDescription>
        </DialogHeader>

        <DialogFooter
          showCloseButton={false}
          className="mx-0 mb-0 mt-0 w-full flex-row justify-end gap-2 rounded-b-[6px] border-t border-border bg-muted/50 px-6 py-4"
        >
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="h-8 w-full px-5 text-[0.75rem] normal-case tracking-normal sm:w-auto"
            disabled={busy}
            data-service-unavailable-retry=""
            onClick={() => void onTryAgain()}
          >
            {busy ? (
              <Loader2 data-icon="inline-start" className="size-3.5 animate-spin" aria-hidden />
            ) : null}
            <span className="normal-case tracking-normal">
              {busy ? "Checking connection…" : "Try again"}
            </span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
